import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Tabs, TabItem, Spinner, Alert, Badge } from "flowbite-react";
import Step2Appraiser from "../components/Steps/Step2Appraiser";
import Step3GoogleMaps from "../components/Steps/Step3GoogleMaps";
import Step4Fema from "../components/Steps/Step4Fema";
import Step5Zillow from "../components/Steps/Step5Zillow";
import Step6Liens from "../components/Steps/Step6Liens";
import StepPhotoGallery from "../components/Steps/StepPhotoGallery";
import { auctionsThunks, countiesThunks, countyLinksThunks } from "../app/store";

import {
  auctionPropertiesActions,
  auctionPropertiesThunks,
} from "../app/store";
import VaReview from "../components/Steps/VaReview";

function FieldRow({ label, value }) {
  return (
    <div className="text-sm">
      <div className="text-gray-500">{label}</div>
      <div className="text-gray-900 dark:text-white font-medium">
        {value || "—"}
      </div>
    </div>
  );
}

export default function AuctionPropertyResearchPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auctionId, propertyId } = useParams();
  const auction = useSelector((s) => {
    const fromById = s.auctions?.byId?.[auctionId] || null;
    const fromCurrent =
      s.auctions?.current?.data?.id === auctionId
        ? s.auctions.current.data
        : null;
    return fromById || fromCurrent;
  });

  const { byId, status, error } = useSelector((s) => s.auctionProperties);
  const property = byId?.[propertyId] || null;
  const county = useSelector((s) =>
    auction?.county_id ? s.counties?.byId?.[auction.county_id] || null : null,
  );

  const isLoading = status === "loading" && !property;

  const [activeTab, setActiveTab] = useState(0);

  const liens = useMemo(() => {
    const all = property?.liens || property?.PropertyLiens || [];
    return [...all].sort(
      (a, b) =>
        new Date(b.recorded_at || b.createdAt) -
        new Date(a.recorded_at || a.createdAt),
    );
  }, [property]);

  useEffect(() => {
    if (!propertyId) return;
    dispatch(auctionPropertiesThunks.getById(propertyId));
  }, [dispatch, propertyId]);

  useEffect(() => {
    if (!auctionId) return;
    dispatch(auctionsThunks.getById(auctionId));
  }, [dispatch, auctionId]);

  const countyLinksStatus = useSelector((s) => s.countyLinks.status);

  useEffect(() => {
    if (!auction?.county_id) return;
    if (!county) {
      dispatch(countiesThunks.getById(auction.county_id));
    }
  }, [dispatch, auction?.county_id, county]);

  useEffect(() => {
    if (!auction?.county_id) return;
    if (countyLinksStatus === "loading") return;

    dispatch(
      countyLinksThunks.list({
        county_id: auction.county_id,
        limit: 50,
        offset: 0,
      }),
    );
  }, [dispatch, auction?.county_id]);

  const countyLinksItems = useSelector((s) => s.countyLinks?.items || []);

    const linksByType = useMemo(() => {
    // pega somente links do county atual (garante)
    const filtered = auction?.county_id
      ? countyLinksItems.filter((l) => l.county_id === auction.county_id)
      : [];

    // cria map por tipo (se tiver mais de 1 do mesmo tipo, pega o is_primary primeiro)
    const map = {};
    for (const l of filtered) {
      if (!l?.link_type) continue;
      if (!map[l.link_type]) {
        map[l.link_type] = l;
        continue;
      }
      // preferir primary
      if (!map[l.link_type].is_primary && l.is_primary) {
        map[l.link_type] = l;
      }
    }
    return map;
  }, [countyLinksItems, auction?.county_id]);

  function clearErr() {
    dispatch(auctionPropertiesActions.clearError());
  }

  const headerTitle = useMemo(() => {
    if (!property) return "Pesquisa da Propriedade";
    return `Parcel ${property.parcel_number || "—"}`;
  }, [property]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {headerTitle}
          </h1>
          <p className="text-sm text-gray-500">
            Auction: <span className="font-medium">{auctionId}</span>
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            color="gray"
            onClick={() => navigate(`/app/auctions/${auctionId}/properties`)}
          >
            Voltar
          </Button>
        </div>
      </div>

      {error && (
        <Alert color="failure" onDismiss={clearErr}>
          {error}
        </Alert>
      )}

      {/* Header legal (passo 1) */}
      <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-gray-900">
        {isLoading ? (
          <div className="flex items-center gap-3">
            <Spinner />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Carregando...
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Badge color={property?.rating ? "info" : "gray"}>
                {property?.rating ? `Rating ${property.rating}` : "Sem rating"}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FieldRow label="Parcel Number" value={property?.parcel_number} />
              <FieldRow label="Owner Name" value={property?.owner_name} />
              <FieldRow label="Address" value={property?.property_address} />
            </div>
          </div>
        )}
      </div>

      {/* Steps 2–6 + Final */}
      <div className="border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900">
        <Tabs
          aria-label="Steps"
          variant="underline"
          onActiveTabChange={(i) => setActiveTab(i)}
        >
          <TabItem active title="2) Appraiser">
            <Step2Appraiser
              propertyId={propertyId}
              property={property}
              county={county}
              onSave={() =>
                dispatch(auctionPropertiesThunks.getById(propertyId))
              }
              appraiserLink={linksByType.PROPERTY_APPRAISER || null}
              linksLoading={countyLinksStatus === "loading"}
            />
          </TabItem>

          <TabItem title="3) Google Maps">
            <Step3GoogleMaps
              propertyId={propertyId}
              property={property}
              onSave={() =>
                dispatch(auctionPropertiesThunks.getById(propertyId))
              }
            />
          </TabItem>

          <TabItem title="4) FEMA">
            <Step4Fema
              propertyId={propertyId}
              property={property}
              onSave={() =>
                dispatch(auctionPropertiesThunks.getById(propertyId))
              }
            />
          </TabItem>

          <TabItem title="5) Valor / Zillow">
            <Step5Zillow
              propertyId={propertyId}
              property={property}
              onSave={() =>
                dispatch(auctionPropertiesThunks.getById(propertyId))
              }
            />
          </TabItem>

          <TabItem title="6) Liens">
            <Step6Liens
              propertyId={propertyId}
              property={property}
              onSave={() =>
                dispatch(auctionPropertiesThunks.getById(propertyId))
              }
              publicRecordsLinks={countyLinksItems.filter((l) => l.county_id === auction?.county_id && l.link_type === "PUBLIC_RECORDS_SEARCH")}
            />
          </TabItem>

          <TabItem title="7) Galeria">
            <StepPhotoGallery
              propertyId={propertyId}
              property={property}
              onSave={() =>
                dispatch(auctionPropertiesThunks.getById(propertyId))
              }
            />
          </TabItem>

          <TabItem title="VA Review">
            <VaReview
              propertyId={propertyId}
              property={property}
              onSave={() =>
                dispatch(auctionPropertiesThunks.getById(propertyId))
              }
            />
          </TabItem>
        </Tabs>
      </div>
    </div>
  );
}

